import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import type {
  VehicleBody,
  BodyListResponse,
  BodyListParams,
  BodyInput,
  AssignmentInput,
} from '@/lib/types';

export async function fetchBodies(params: BodyListParams): Promise<BodyListResponse> {
  const { data } = await apiClient.get<BodyListResponse>('/api/v1/bodies', { params });
  return data;
}

export async function fetchBody(id: string): Promise<VehicleBody> {
  const { data } = await apiClient.get<{ body: VehicleBody }>(`/api/v1/bodies/${id}`);
  return data.body;
}

export async function createBody(input: BodyInput): Promise<VehicleBody> {
  const { data } = await apiClient.post<{ body: VehicleBody }>('/api/v1/bodies', input);
  return data.body;
}

export async function updateBody(id: string, input: Partial<BodyInput>): Promise<VehicleBody> {
  const { data } = await apiClient.patch<{ body: VehicleBody }>(`/api/v1/bodies/${id}`, input);
  return data.body;
}

export async function assignBody(input: AssignmentInput) {
  const { data } = await apiClient.post('/api/v1/bodies/assign', input);
  return data;
}

export async function unassignBody(vehicleId: string) {
  const { data } = await apiClient.post('/api/v1/bodies/unassign', { vehicleId });
  return data;
}

export function useBodies(params: BodyListParams) {
  return useQuery({
    queryKey: ['bodies', params],
    queryFn: () => fetchBodies(params),
    placeholderData: (prev) => prev,
  });
}

export function useAvailableBodies() {
  // Bodies that are not currently assigned (can be mounted on a truck)
  return useQuery({
    queryKey: ['bodies-available'],
    queryFn: async () => {
      const { data } = await apiClient.get<BodyListResponse>('/api/v1/bodies', {
        params: { status: 'active', pageSize: 100 },
      });
      // Filter to ones with no active assignment
      return data.items.filter((b) => !b.assignments || b.assignments.length === 0);
    },
    staleTime: 30 * 1000,
  });
}

export function useCreateBody() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createBody,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bodies'] });
      qc.invalidateQueries({ queryKey: ['bodies-available'] });
    },
  });
}

export function useUpdateBody() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<BodyInput> }) =>
      updateBody(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bodies'] });
    },
  });
}

export function useAssignBody() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: assignBody,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bodies'] });
      qc.invalidateQueries({ queryKey: ['bodies-available'] });
      qc.invalidateQueries({ queryKey: ['vehicles'] });
      qc.invalidateQueries({ queryKey: ['vehicle'] });
    },
  });
}

export function useUnassignBody() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: unassignBody,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bodies'] });
      qc.invalidateQueries({ queryKey: ['bodies-available'] });
      qc.invalidateQueries({ queryKey: ['vehicles'] });
      qc.invalidateQueries({ queryKey: ['vehicle'] });
    },
  });
}
